'use client';

import { useState } from 'react';

/**
 * The gate.
 *
 * Says nothing about what is behind it: no product name, no company, no hint
 * of what the environment does. Whoever finds this address should not learn
 * that it belongs to a content-protection service, which of our environments
 * it is, or that there is anything worth attacking. An anonymous door is a
 * smaller target than a labelled one.
 *
 * The same reticence applies to failures: a wrong password says only that it
 * was wrong.
 *
 * The one place in this codebase where inline style objects are the right
 * answer, and the reason is the paragraph above. Every class in
 * `globals.css` is this product's design system: its violet, its radii, its
 * two typefaces. A door drawn out of them is a door that says which product
 * is behind it to anybody who reads one rule of the stylesheet. So this page
 * shares nothing with the app it guards — its own greys, its own borders, a
 * system font stack rather than the webfonts, and a lock drawn in the markup
 * so there is no asset whose failure to load could leave it looking broken.
 * Do not "tidy" these into classes.
 */
export function GatePage() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(false);

    const response = await fetch('/gate/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    if (response.ok) {
      window.location.reload();
      return;
    }

    setError(true);
    setPassword('');
    setBusy(false);
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: '#0c0d10',
        color: '#e6e7ea',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
      }}
    >
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 320, textAlign: 'center' }}>
        {/* A plain lock, drawn inline: no logo, and no asset that could fail to
            load and leave the page looking broken. */}
        <svg width="30" height="30" viewBox="0 0 24 24" aria-hidden style={{ marginBottom: 20 }}>
          <rect x="4.5" y="10.5" width="15" height="10" rx="2" fill="none" stroke="#5b6070" strokeWidth="1.5" />
          <path d="M8.2 10.5V7.6a3.8 3.8 0 0 1 7.6 0v2.9" fill="none" stroke="#5b6070" strokeWidth="1.5" />
        </svg>

        <label htmlFor="k" style={{ display: 'block', marginBottom: 12, fontSize: 14, color: '#7c8290' }}>
          Restricted
        </label>

        <input
          id="k"
          type="password"
          autoFocus
          autoComplete="off"
          aria-label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{
            width: '100%',
            padding: '11px 14px',
            borderRadius: 8,
            border: `1px solid ${error ? '#7a3b3b' : '#23262f'}`,
            background: '#141720',
            color: '#e6e7ea',
            fontSize: 15,
            textAlign: 'center',
            letterSpacing: '0.08em',
            outline: 'none',
          }}
        />

        <button
          type="submit"
          disabled={busy || password.length === 0}
          style={{
            width: '100%',
            marginTop: 12,
            padding: '11px 16px',
            borderRadius: 8,
            border: 'none',
            background: password.length === 0 ? '#1b1e26' : '#3b4252',
            color: password.length === 0 ? '#5b6070' : '#e6e7ea',
            fontSize: 15,
            cursor: password.length === 0 ? 'default' : 'pointer',
          }}
        >
          {busy ? '…' : 'Continue'}
        </button>

        {/* Deliberately vague: naming the reason helps whoever is guessing. */}
        <p
          role={error ? 'alert' : undefined}
          style={{
            margin: '14px 0 0',
            minHeight: 18,
            fontSize: 13,
            color: '#7a5252',
            visibility: error ? 'visible' : 'hidden',
          }}
        >
          Incorrect.
        </p>
      </form>
    </main>
  );
}
